import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import DoseColorwashSlider from './DoseColorwashSlider';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';
import ThemeProvider from '~/theme/ThemeProvider';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { usePlaneViewOf } from '../usePlaneViewOf';
import { SeriesInstanceUid, seriesInstanceUidOf } from '~/domain/dicom/uids';

interface TileDoseColorwashMountProps {
    renderer: RenderAdapter;
    /** The images the tile leads, as series instance uids. */
    ledImages: ReadonlyArray<SeriesInstanceUid>;
}

/**
 * Mounts the dose colorwash control (visibility, window, go to max) at the
 * top-left of the tile that leads its row's own image, when the row's
 * Environment draws the dose. The dose is drawn where that image is, so
 * the control sits on a tile that shows what it changes and never on a
 * tile that draws the fused image alone. Each row that draws the dose
 * carries its own control; all of them read and write the shared display
 * atoms, which reach every drawn dose, so they always agree. The control
 * is drawn under the app theme, since the grid's console chrome has no
 * overlay glass. Renders nothing for non-engine renderers (the mock
 * adapters).
 */
export default function TileDoseColorwashMount({
    renderer,
    ledImages,
}: TileDoseColorwashMountProps) {
    // A dose load or unload replaces the Environment's dose without a
    // registry notification; the selected dose id is what re-reads it.
    useRecoilValue(currentDoseIdState);
    const environment = usePlaneViewOf(renderer)?.environment;
    const rowSeriesUid = seriesInstanceUidOf(
        environment?.image?.parent.seriesUid
    );
    if (!environment?.dose || !ledImages.some((uid) => uid === rowSeriesUid)) {
        return null;
    }
    return (
        <Box
            data-testid="tile-dose-colorwash"
            sx={{ position: 'absolute', left: 12, top: 8 }}
        >
            <ThemeProvider>
                <DoseColorwashSlider />
            </ThemeProvider>
        </Box>
    );
}
